import { useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { Lock, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const input = "w-full rounded-xl border border-zinc-700 bg-[#0B0D13] px-4 py-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const TITLES = { login: "Autentificare", register: "Cere un cont", forgot: "Am uitat parola", code: "Cod de verificare", reset: "Parolă nouă" };
const SUBMIT = { login: "Intră", register: "Trimite cererea", forgot: "Trimite linkul", code: "Verifică", reset: "Salvează parola" };
const NEEDS_BOT_CHECK = ["login", "register", "forgot"];
const resetToken = () => new URLSearchParams(window.location.search).get("reset");

export default function Login({ onSuccess }) {
  const [mode, setMode] = useState(resetToken() ? "reset" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [challenge, setChallenge] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const widget = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const go = (m, msg = "") => { setMode(m); setError(""); setInfo(msg); };

  const actions = {
    login: async () => {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password, turnstile_token: token });
      if (data.two_factor) { setChallenge(data); setForm((f) => ({ ...f, code: "" })); go("code", `Ți-am trimis un cod de 6 cifre pe ${data.email}.`); } else onSuccess(data);
    },
    register: async () => { const { data } = await api.post("/auth/register", { ...form, turnstile_token: token }); go("login", data.message); },
    forgot: async () => { const { data } = await api.post("/auth/forgot-password", { email: form.email, turnstile_token: token }); go("login", data.message); },
    code: async () => onSuccess((await api.post("/auth/verify-code", { challenge_id: challenge.challenge_id, code: form.code })).data),
    reset: async () => {
      const { data } = await api.post("/auth/reset-password", { token: resetToken(), new_password: form.password });
      window.history.replaceState(null, "", "/");
      setForm((f) => ({ ...f, password: "" }));
      go("login", data.message);
    },
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (NEEDS_BOT_CHECK.includes(mode) && !token) return setError("Așteaptă verificarea anti-bot.");
    setBusy(true);
    try { await actions[mode](); } catch (err) { setError(errMsg(err)); } finally {
      if (NEEDS_BOT_CHECK.includes(mode)) { setToken(""); widget.current?.reset(); }
      setBusy(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-code", { challenge_id: challenge.challenge_id }); setInfo("Am trimis un cod nou."); setError(""); } catch (err) { setError(errMsg(err)); }
  };

  const needsBot = NEEDS_BOT_CHECK.includes(mode);
  const showEmail = ["login", "register", "forgot"].includes(mode);
  const showPassword = ["login", "register", "reset"].includes(mode);

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-zinc-800 bg-[#12141C] p-7" data-testid="login-form">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500"><Lock className="h-5 w-5 text-[#0B0D13]" /></div>
        <h1 className="font-display text-2xl font-bold" data-testid="login-title">{TITLES[mode]}</h1>
        {mode === "register" && <input required maxLength={80} autoComplete="name" placeholder="Nume și prenume" value={form.name} onChange={set("name")} className={input} data-testid="register-name-input" />}
        {showEmail && <input required type="email" maxLength={200} autoComplete="email" placeholder="Email" value={form.email} onChange={set("email")} className={input} data-testid="login-email-input" />}
        {showPassword && <input required type="password" maxLength={200} minLength={mode === "login" ? undefined : 10} autoComplete={mode === "login" ? "current-password" : "new-password"}
          placeholder={mode === "login" ? "Parolă" : "Parolă nouă (min. 10 caractere, litere și cifre)"} value={form.password} onChange={set("password")} className={input} data-testid="login-password-input" />}
        {mode === "code" && <input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" placeholder="123456" value={form.code}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "") }))} className={`${input} text-center font-mono text-2xl tracking-[0.5em]`} data-testid="login-code-input" />}
        {needsBot && <Turnstile key={mode} ref={widget} siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY} options={{ action: mode, theme: document.documentElement.classList.contains("light") ? "light" : "dark", size: "flexible" }}
          onSuccess={setToken} onExpire={() => setToken("")} onError={() => { setToken(""); setError("Verificarea anti-bot a eșuat. Reîncarcă pagina."); }} />}
        {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
        {info && <p className="text-sm text-emerald-400" data-testid="login-info">{info}</p>}
        <button disabled={busy || (needsBot && !token)} data-testid="login-submit-button" className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 font-semibold text-[#0B0D13] hover:bg-amber-400 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{SUBMIT[mode]}
        </button>
        <div className="flex flex-col items-center gap-2 text-sm text-zinc-400">
          {mode === "login" && <button type="button" onClick={() => go("forgot")} data-testid="login-forgot-link" className="hover:text-zinc-200">Am uitat parola</button>}
          {mode === "code" && <button type="button" onClick={resend} data-testid="login-resend-code" className="hover:text-zinc-200">Retrimite codul</button>}
          {mode === "login" && <button type="button" onClick={() => go("register")} data-testid="login-mode-toggle" className="hover:text-zinc-200">Nu ai cont? Cere unul</button>}
          {mode !== "login" && <button type="button" onClick={() => { window.history.replaceState(null, "", "/"); go("login"); }} data-testid="login-back-link" className="hover:text-zinc-200">Înapoi la autentificare</button>}
        </div>
      </form>
    </div>
  );
}
